import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";

import DeletePopUp from "../../components/popups/DeletePopUp";
import Filter from "../../components/order/ListFilter";
import Pagination from "../../components/shared/Pagination";
import TableList from "../../components/shared/TableList";
import StatusBadge from "../../components/shared/StatusBadge";
import { TextCell, NumberCell } from "../../components/shared/TableCell";

import useQueryString from "../../hooks/useQueryString";
import useRefetch from "../../hooks/useRefetch";

import { deleteDataFrom, fetchDataFrom } from "../../_helper/fetcher";
import AssignPicker from "../../components/order/AssignPicker";
import { toast } from "react-toastify";
import { makeItSelectable, exportOrdersToCSV } from "../../_helper/helper";
import ConfirmOrCancelModal from "../../components/order/ConfirmOrCancelModal";
import useButtons from "../../hooks/useButtons";

const columnsHead = [
  { name: "customer's name", accessor: ["billing_name"] },
  { name: "customer's phone", accessor: ["billing_phone"] },
  { name: "delivery fee", accessor: ["delivery_price", "yesItsPrice"] },
  { name: "summary price", accessor: ["summary_price", "yesItsPrice"] },
  { name: "payment method", accessor: ["payment_method", "splitJoinFunc"] },
];

export default function OrdersList() {
  const [showDeleteModal, setShowDeleteModal] = useState({
    id: "",
    show: false,
  });
  const [showAssignModal, setShowAssignModal] = useState({
    show: false,
    id: null,
  });
  const [showConfirmModal, setShowConfirmModal] = useState({
    show: false,
    order: null,
    type: "confirm",
  });
  const [pagin, setPagin] = useState({});
  const [pickers, setPickers] = useState([]);

  const navigate = useNavigate();
  const { refetchList } = useRefetch();
  const { queryString } = useQueryString();

  const getData = async () => {
    const res = await fetchDataFrom("orders" + queryString);
    if (res.error) return null;
    const items = res.data;
    const { per_page, pages, total } = res.meta;
    setPagin({
      pages: pages,
      perPage: per_page,
      total: total,
    });
    return items;
  };
  const getReport = async () => {
    try {
      toast.info("Fetching orders for export...");
      
      // Parse current queryString to extract filters
      const urlParams = new URLSearchParams(queryString ? queryString.replace('?', '') : '');
      
      // Remove pagination params and set high limit to get all orders
      urlParams.delete('page');
      urlParams.set('limit', '10000'); // Get all orders (adjust if you have more)
      
      const exportQueryString = '?' + urlParams.toString();
      
      // Fetch all orders matching the filters
      const res = await fetchDataFrom("orders" + exportQueryString);
      
      if (res.error) {
        toast.error(res.message || "Failed to fetch orders");
        return;
      }
      
      const ordersToExport = res.data || [];
      
      if (ordersToExport.length === 0) {
        toast.warning("No orders to export");
        return;
      }
      
      // Export to CSV directly
      exportOrdersToCSV(ordersToExport);
      toast.success(`Successfully exported ${ordersToExport.length} orders to CSV`);
      
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export CSV: " + (error.message || "Unknown error"));
    }
  };

  async function getPickers() {
    try {
      const res = await fetchDataFrom("admins?role_id=13");
      setPickers(makeItSelectable(res.data));
    } catch (error) {
      toast.error(error.message);
    }
  }
  useEffect(() => {
    getPickers();
  }, []);

  const deleteProduct = async () => {
    await deleteDataFrom("orders/" + showDeleteModal.id);
    setShowDeleteModal({ id: "", show: false });
    refetchList("orders");
  };

  const { ExportButton } = useButtons();

  const {
    data: orders,
    isLoading,
    isError,
    error,
  } = useQuery(["orders", queryString], getData);
  const { mutate, isLoading: isExportLoading } = useMutation({
    mutationFn: getReport,
    onerror: () => toast.error("Failed to export orders report"),
  });
  return (
    <>
      <div className="px-2">
        <div className="box">
          <div className="box-content p-0">
            <div className="box-tools">
              <div className="box-title">
                <h3>Orders</h3>
              </div>
              <ExportButton
                permission="view_order"
                onClick={mutate}
                isLoading={isExportLoading}
              />
            </div>
            <Filter />
            <TableList
              editPer="edit_order"
              data={orders}
              columns={columnsHead}
              onDetail={(item) => navigate("/orders/" + item.id)}
              isLoading={isLoading}
              isError={isError}
              error={error}
              customColumns={[{ name: "status" }, { name: "Order Request" }]}
              customFirstColumns={[{ name: "Order Number" }]}
              customElements={[<Status />, <OrderRequest />]}
              customFirstElements={[<OrderNumber />]}
              // onCustomClick={handleProductClicked}
              customButton={
                <ConfirmOrAssign
                  onConfirm={(type, order) =>
                    setShowConfirmModal({ show: true, order, type })
                  }
                  onClickAssign={(id) => setShowAssignModal({ show: true, id })}
                  refetch={() => refetchList("orders")}
                />
              }
            />
          </div>
        </div>
      </div>
      {showAssignModal.show && (
        <AssignPicker
          pickers={pickers}
          orderID={showAssignModal.id}
          close={() => setShowAssignModal({ show: false, id: null })}
          refetch={() => refetchList("orders")}
        />
      )}
      {showConfirmModal.show && (
        <ConfirmOrCancelModal
          order={showConfirmModal.order}
          type={showConfirmModal.type}
          close={() =>
            setShowConfirmModal({ show: false, order: null, type: "confirm" })
          }
          refetch={() => refetchList("orders")}
        />
      )}
      {showDeleteModal.show && (
        <DeletePopUp
          close={() => setShowDeleteModal({ id: "", show: false })}
          func={deleteProduct}
          text="Click the 'Delete' button to delete this product"
        />
      )}

      {pagin?.perPage <= pagin?.total && <Pagination pagin={pagin} />}
    </>
  );
}

function ConfirmOrAssign({ item, onClickAssign, onConfirm }) {
  const { status, payment_method } = item;
  return (
    <>
      {status === "pending" && payment_method === "COD" && (
        <button
          onClick={() =>
            onConfirm("confirm", {
              id: item.id,
              order_number: item?.order_number,
            })
          }
          className="btn btn-sm btn-info mr-1"
        >
          Confrim
        </button>
      )}
      {status !== "reject" && payment_method === "COD" && (
        <button
          onClick={() =>
            onConfirm("cancel", {
              id: item.id,
              order_number: item?.order_number,
            })
          }
          className="btn btn-sm btn-danger mr-1"
        >
          Cancel
        </button>
      )}
      {["confirmed", "confirm"].includes(status) && (
        <button
          onClick={() => onClickAssign(item.id)}
          className="btn btn-sm btn-primary mr-1"
        >
          Assign Picker
        </button>
      )}
    </>
  );
}

function OrderRequest({ item }) {
  return (
    <TextCell 
      primary={item?.order_request_status === true ? "Yes" : "No"} 
    />
  );
}
function OrderNumber({ item }) {
  return (
    <Link className="underline" to={`/orders/${item.id}`}>
      {item.order_number}
    </Link>
  );
}

export function Status({ item: { status } }) {
  return <StatusBadge status={status} />;
}
